/* eslint-disable object-curly-newline */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Table, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import selectSelectedRecordDetails from '../../selectors/selectedRecordDetails';
import { getIcon } from './constants';
import './inbox.scss';
import { markRecordAsViewed, setSelectedRecordDetails  } from '../../actions/records';
import { captureAnalytics } from '../../actions/preload';
import buildRecordViewerPath from '../../util/buildRecordViewerPath';
import { CAPABILITIES, rowLacksCapability, opensMessageDrawerOnClick } from '../../util/inboxRowCapabilities';

export class InboxTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedRowKeys: [],
      enableSelectAll: props.enableSelectAll || false,
      keyParam: 'file_token',
    };
  }

  componentWillMount() {
    const { mode } = this.props;
    if (mode === 'Uploaded') {
      this.setState({ keyParam: 'file_token' });
    } else if (mode === 'Received' || mode === 'Sent') {
      this.setState({ keyParam: 'send_token' });
    } else if (mode === 'Assigned') {
      this.setState({ keyParam: 'slug' });
    }
  }

  // Opt-in (redesigned surfaces only): reconcile the local checkbox selection
  // from the Redux selection slice so external mutations stay visible. The
  // redesigned RowQuickActions menu narrows the selection to a single record on
  // open; without this the checkbox column would drift from the toolbar/state.
  // Legacy surfaces never pass syncSelectionFromStore, so their behavior is
  // unchanged.
  componentDidUpdate(prevProps) {
    const { syncSelectionFromStore, selectedData, rows } = this.props;
    if (!syncSelectionFromStore) return;
    if (prevProps.selectedData === selectedData) return;

    const { keyParam, selectedRowKeys } = this.state;
    const nextKeys = (Array.isArray(selectedData) && selectedData[0] === 'all')
      ? (rows || []).map((row) => row[keyParam]).filter((key) => key != null)
      : (selectedData || [])
        .map((row) => (row ? row[keyParam] : null))
        .filter((key) => key != null);

    const unchanged = nextKeys.length === selectedRowKeys.length
      && nextKeys.every((key, index) => key === selectedRowKeys[index]);
    if (!unchanged) {
      this.setState({ selectedRowKeys: nextKeys });
    }
  }

  /**
   * Handle row selection/deselection in the table
   * This will enable/disable the buttons in the header
   * @param  {Array} selectedRowKeys array of this.state.key_param strings indicating
   * which rows selected
   * @param {Array} selectedRowData array of dataSource row objects selected
   * @return {Void}
   */
  handleRowClick = (selectedRowKeys, selectedRowData) => {
    let updatedSelectedRowKeys = selectedRowKeys;
    let updatedSelectedRowData = selectedRowData;
    // The sent records inbox should only allow one record to be selected at a time
    //
    // This is because currently, selecting a record in the sent inbox is used to
    // resend exactly 1 record. When the backend can support multiple, we should be able
    // to safely remove this code.
    //
    // We only worry about this if 1 record is already selected. We will then change the
    // selected record to the newer one
    const { mode, rowSelected, userIsOnChromeExtensionViewer } = this.props;
    const disableMultipleSelection = mode === 'Sent' || userIsOnChromeExtensionViewer;
    if (disableMultipleSelection && selectedRowKeys.length > 1) {
      const {
        selectedRowKeys: previousSelectedRowKeys,
        keyParam,
      } = this.state;
      // Since a row is already selected, selectedRowKeys will contain two keys. But the inbox
      // can should only have one selected at a time, so we will figure out what to set as the
      // new one by determining which key was previously set and removing it.
      updatedSelectedRowKeys = selectedRowKeys.filter(rowKey => !previousSelectedRowKeys.includes(rowKey));
      // We need to pass the new selected row's data and store in redux, so index in and get it
      const newSelectedRowKey = updatedSelectedRowKeys[0];
      updatedSelectedRowData = selectedRowData.filter(rowData => rowData[keyParam] === newSelectedRowKey);
    }
    // Store rows selected in Redux for other options, like merging
    rowSelected(updatedSelectedRowData);
    // Update local state
    this.setState({ selectedRowKeys: updatedSelectedRowKeys });
  }

  /**
   *
   * @param {Boolean} selected whether the select all checkbox was clicked
   * @param {Array} selectedRows array of dataSource rows that were requested
   * to be selected
   * @param {Array} changeRows array of dataSource rows that were not previously
   * selected (selectedRows - changeRows) = rows that were selected  before
   */
  onSelectAll = (selected, selectedRows, changeRows) => {
    if (selected) {
      // If a user has multiple rows selected (but not all) and then clicks
      // the select all checkbox, we will de-select every row
      // that has been selected.
      if (selectedRows.length !== changeRows.length) {
        return this.setState({ selectedRowKeys: [] });
      }
    }
  }

  /**
   * Toggle selection for a single row
   */
  toggleRowSelection = (record) => {
    const { selectedRowKeys, keyParam } = this.state;
    const { mode, userIsOnChromeExtensionViewer, rows } = this.props;
    const rowKey = record[keyParam];
    const isCurrentlySelected = selectedRowKeys.includes(rowKey);

    let newSelectedKeys;

    if (isCurrentlySelected) {
      newSelectedKeys = selectedRowKeys.filter((key) => key !== rowKey);
    } else {
      // Respect single-select mode for Sent page and Chrome extension
      const disableMultipleSelection = mode === 'Sent' || userIsOnChromeExtensionViewer;
      newSelectedKeys = disableMultipleSelection
        ? [rowKey]
        : [...selectedRowKeys, rowKey];
    }

    // Get all selected rows' data from the rows prop
    const newSelectedData = rows.filter((row) => newSelectedKeys.includes(row[keyParam]));

    this.handleRowClick(newSelectedKeys, newSelectedData);
  }

  handleTableChange = (pages) => {
    const { actions, paramTools } = this.props;
    const { params, stringify, stringifyOptions } = paramTools;
    const { getRecords } = actions;
    params.page = pages.current;
    if (pages.pageSize) {
      params.page_size = pages.pageSize;
    }
    getRecords(stringify(params, stringifyOptions));
  }

  // The row-click steps that open a record in the slide-out viewer. Shared by the row's
  // own click and by the comment indicator (ENG-1962), so both open a record the same way.
  openRow = (record, index) => {
    const {
      // eslint-disable-next-line no-shadow
      setSelectedRecordDetails, markRecordAsViewed, markRecordAsReceived, setNewRecordViewerOpen,
    } = this.props;
    // keyParam is necessary when sending data to handleRowClick because when mode is
    // Received, the data is indexed into using a distinct keyParam, file_token
    setSelectedRecordDetails(record);
    if (markRecordAsReceived && !record.viewed) {
      markRecordAsViewed(record.send_token);
    }
    setNewRecordViewerOpen(index);
  };

  // ENG-1962: open the viewer on the Comments tab. An explicit control, like the SMS and
  // reply icons, so unlike a row click it opens even while several rows are selected and
  // leaves that selection alone. When the viewer already shows this record only the tab
  // and focus request is repeated: no re-select, no comments refetch.
  // "This record" is the send_token the viewer loads comments by, not keyParam: in Referral
  // Management keyParam is file_token, which Receiver rows of one Record share.
  openRowComments = (record, index) => {
    const { selectedRecordDetails, viewerOpen, requestViewerComments } = this.props;
    const alreadyOpen = viewerOpen && Boolean(record.send_token)
      && record.send_token === selectedRecordDetails?.send_token;
    if (!alreadyOpen) this.openRow(record, index);
    requestViewerComments();
  };

  renderLink(row, tooltipPosition) {
    const icon = getIcon(row);
    const patientQuery = row.patient ? '?patient=true' : '';
    const iconStyle = {
      color: icon.color,
      top: '7px',
      marginTop: '-15px',
      left: '8px',
      height: '17px',
      width: '17px',
    };
    return (
      <>
        <Link to={`/app/records/status/record/${row.file_token}/receiver/${row.send_token + patientQuery}`}>
          <div className="status-badge">Status</div>
        </Link>
        <Tooltip title={icon.text} placement={(tooltipPosition || 'bottom-center').split('-')[0]}>
          <span className={`material-icons ${row.fax_status}`} style={iconStyle}>{icon.name}</span>
        </Tooltip>
      </>
    );
  }

  render() {
    const {
      columnTitles,
      rows,
      totalResults,
      page,
      countState,
      mode,
      userIsOnChromeExtensionViewer,
      getReplyData,
      getSmsData,
      getReferralReplyData,
      paramTools,
      push,
      selectedRecordDetails,
      renderSelectionCell,
    } = this.props;
    const { selectedRowKeys, keyParam, enableSelectAll } = this.state;
    const activeRowKey = selectedRecordDetails ? selectedRecordDetails[keyParam] : null;
    const rowSelection = {
      selectedRowKeys,
      onChange: (this.handleRowClick),
      onSelectAll: (this.onSelectAll),
      hideSelectAll: !enableSelectAll,
    };
    // Opt-in (redesigned surfaces only): render a per-row quick-actions trigger
    // inside the checkbox cell instead of adding a separate column. Legacy
    // tables never pass renderSelectionCell, so their selection column is
    // untouched.
    if (typeof renderSelectionCell === 'function') {
      rowSelection.renderCell = renderSelectionCell;
      // Wide enough to hold the checkbox + the reserved caret slot to its right
      // without clipping (see .rq-selection-cell / .rq-cell in the stylesheet).
      rowSelection.columnWidth = 72;
    }
    const asyncCountPending = countState === 'async';
    return (
      <Table
        dataSource={rows}
        onChange={this.handleTableChange}
        rowSelection={rowSelection}
        handleTableChange={this.handleTableChange}
        rowClassName={(record) => {
          const classes = [];
          if (record.viewed) classes.push('record-row-viewed');
          if (record[keyParam] === activeRowKey) classes.push('record-row-active');
          if (record.isReferralChildRow) classes.push('referral-child-row');
          // Keep the tutorial marker tied to assignment capability, including
          // ready Reply rows whose organization has enabled assignment.
          // Marked on the row so a caller can tell from the DOM which rows an action will
          // refuse, rather than having to select one to find out. Used by the "Assign a
          // record" tutorial to spotlight a row that can actually be assigned.
          if (rowLacksCapability(record, CAPABILITIES.ASSIGN)) classes.push('record-row-reply');
          if (record.receiver_type === 'jotform_submission') classes.push('record-row-form-submission');
          return classes.join(' ');
        }}
        onRow={(record, index) => {
          const {
            uri,
            send_token: sendToken,
            file_token: fileToken,
            slug: assignDocumentId,
            sms_uri: smsUri,
            referral_reply_uri: referralReplyUri,
          } = record;
          const { getReplyData, getSmsData, getReferralReplyData, push } = this.props;
          // Add the functions to the record object
          record.getSmsData = getSmsData;
          record.getReferralReplyData = getReferralReplyData;
          // ENG-2825: the explicit Message/History control a reply row renders (see
          // receivedInboxIcons) opens the same drawer a row click used to, so a row whose
          // click now follows document conventions keeps its history one click away.
          // eslint-disable-next-line no-param-reassign
          record.openMessageHistory = uri ? () => getReplyData(uri, sendToken, record) : undefined;
          // ENG-1962: the comment indicator in the referral icon cell.
          // eslint-disable-next-line no-param-reassign
          record.openComments = () => this.openRowComments(record, index);
          return {
            // Add an event listener to each row within the table. This is so that
            // when a row is clicked, it can be selected without having to click
            // into the checkbox area. Functionality inspired by Dropbox.
            onClick: (event) => {
              // Record titles and patient names are real links. A modified click
              // belongs to the browser so it can open that link in a new tab;
              // allowing it to bubble into the row handler also opens the inline
              // viewer in the current tab.
              if (
                (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
                && event.target.closest('a[href]')
              ) {
                captureAnalytics('record_viewer_link_modified_clicked', { inbox: mode.toLowerCase() });
                return;
              }

              const selectedText = window.getSelection().toString();
              if (selectedText) return;

              // Check if click is in the selection zone (checkbox column)
              if (event.target.closest('.ant-table-selection-column')) {
                // Don't double-toggle if clicking directly on checkbox
                if (!event.target.closest('.ant-checkbox-wrapper')) {
                  this.toggleRowSelection(record);
                }
                return;
              }

              // Chrome extension selector: toggle row selection on click. Tested BEFORE the
              // drawer branch below, as onDoubleClick already does: the selector's completion
              // path is selection followed by Choose, and a reply row used to be swallowed
              // by the drawer branch here so it could never be chosen (ENG-2825, round 2 F12).
              if (userIsOnChromeExtensionViewer) {
                const rowKey = record[keyParam];
                const isSelected = selectedRowKeys.includes(rowKey);
                const newSelectedKeys = isSelected
                  ? selectedRowKeys.filter(key => key !== rowKey)
                  : [rowKey]; // Single selection for Chrome extension
                const newSelectedData = isSelected ? [] : [record];
                this.handleRowClick(newSelectedKeys, newSelectedData);
                return;
              }

              if (opensMessageDrawerOnClick(record)) {
                // Open up secure reply if a row with a reply is clicked -- a legacy reply,
                // or a return whose complete document is not ready yet. A ready complete
                // document follows the ordinary selection path below (ENG-2825).
                // The full row rides along so the drawer can show the
                // reply-level chart status (ENG-3012).
                getReplyData(uri, sendToken, record);
                return;
              }

              // Don't allow selecting rows by clicking if more than 1 is
              // already selected
              if (selectedRowKeys.length > 1) return;
              this.openRow(record, index);
            },
            // Double clicking on the row will open up the <RecordViewer> outside the Chrome selector
            // ~Inspired by Dropbox~
            onDoubleClick: () => {
              // The Chrome selector's completion path is row selection followed by
              // the Choose button. Opening the viewer here strands EHR users in the
              // selector popup instead of returning the document to the extension.
              if (userIsOnChromeExtensionViewer) {
                // A browser double-click emits two click events first, which can
                // toggle the row back off. Restore the intended single selection
                // so Choose remains enabled.
                this.handleRowClick([record[keyParam]], [record]);
                captureAnalytics('chrome_selector_record_double_click_ignored', { mode });
                return;
              }

              // Message-only reply rows are handled by a single click, so ignore double clicks.
              // A ready complete document opens in the viewer like any other row (ENG-2825).
              if (opensMessageDrawerOnClick(record)) return;

              push(buildRecordViewerPath(record, { mode, viaChromeExtension: userIsOnChromeExtensionViewer }));
            },
          };
        }}
        rowKey={record => record[keyParam]}
        pagination={{
          position: ['bottomCenter'],
          showSizeChanger: ![
            '/app/chrome/received_selector',
            '/app/chrome/uploaded_selector',
          ].includes(window.location.pathname),
          defaultPageSize: userIsOnChromeExtensionViewer ? 5 : 10,
          pageSizeOptions: [10, 20, 50],
          pageSize: userIsOnChromeExtensionViewer ? 5 : parseInt(this.props.paramTools.params.page_size, 10) || 10,
          total: totalResults,
          current: page,
          itemRender: (paginationPage, type, originalElement) => {
            if (!asyncCountPending) return originalElement;
            if (type === 'page' || type === 'jump-prev' || type === 'jump-next') return null;
            return originalElement;
          },
          showTotal: (total, range) => {
            // When rendered in the <ChromeExtensionReceived> component, some rows
            // get filtered out because replies are not displayed, despite being retrieved
            // from the backend. When this happens, there appears to be a mismatch of
            // the range of records which are displayed. They don't account for the filtered records,
            // so in an effort to be simple, we simply won't display any text when userIsOnChromeExtensionViewer is true.
            if (userIsOnChromeExtensionViewer) return '';
            const footerText = asyncCountPending
              ? `Viewing ${range[0]} - ${range[1]} records`
              : `Viewing ${range[0]} - ${range[1]} of ${total} records`;

            return (
              <span role="status" aria-live="polite">
                {footerText}
              </span>
            );
          },
        }}
        columns={columnTitles}
      />
    );
  }
}

InboxTable.defaultProps = {
  userIsOnChromeExtensionViewer: false,
  countState: 'exact',
  hasMore: false,
  selectedData: [],
  syncSelectionFromStore: false,
  renderSelectionCell: null,
  viewerOpen: false,
};

InboxTable.propTypes = {
  rows: PropTypes.array.isRequired,
  columnTitles: PropTypes.array.isRequired,
  actions: PropTypes.shape({
    changeInboxPage: PropTypes.func.isRequired,
    getRecords: PropTypes.func.isRequired,
  }).isRequired,
  totalResults: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  countState: PropTypes.oneOf(['exact', 'async']),
  getReplyData: PropTypes.func.isRequired,
  getSmsData: PropTypes.func.isRequired,
  getReferralReplyData: PropTypes.func.isRequired,
  rowSelected: PropTypes.func.isRequired,
  selectedData: PropTypes.array,
  syncSelectionFromStore: PropTypes.bool,
  renderSelectionCell: PropTypes.func,
  paramTools: PropTypes.object.isRequired,
  enableSelectAll: PropTypes.bool.isRequired,
  mode: PropTypes.string.isRequired,
  userIsOnChromeExtensionViewer: PropTypes.bool,
  push: PropTypes.func.isRequired,
  setNewRecordViewerOpen: PropTypes.func.isRequired,
  // ENG-1962: asks the viewer to show Comments and focus "Add a comment".
  requestViewerComments: PropTypes.func.isRequired,
  viewerOpen: PropTypes.bool,
  setSelectedRecordDetails: PropTypes.func.isRequired,
  selectedRecordDetails: PropTypes.object,
  markRecordAsViewed: PropTypes.func.isRequired,
};

// eslint-disable-next-line no-unused-vars
export default connect(state => ({
  selectedRecordDetails: selectSelectedRecordDetails(state),
}), {
  setSelectedRecordDetails,
  markRecordAsViewed,
})(InboxTable);
